import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import PickerField from '../../../../components/PickerField';
import { useNotifier } from '../../../../components/Notifier';
import { createTireTransaction } from '../../../../stores/tireStore';
import { useTireDamageDataList } from '../../../../stores/tireDamageStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { useLanguage } from '../../../../utils/i18n';

const buildReasonArgs = (state) => [state.page, 1000, [], state.searchKey, state.searchValue];
const formatReasonOption = (r) => ({
  value: r.id,
  label: r.name
});

const getValidValueWithSource = (v1, v2, v3) => {
  if (v1 != null && v1 !== 0 && String(v1) !== '0') return { value: v1, source: 'Last Inspection' };
  if (v2 != null && v2 !== 0 && String(v2) !== '0') return { value: v2, source: 'Base' };
  if (v3 != null && v3 !== 0 && String(v3) !== '0') return { value: v3, source: 'Standard' };
  return { value: null, source: null };
};

export default function RemoveForm({
  vehicleDetails,
  tireEquipVehicleId,
  selectedTires,
  installedTires,
  rawAxleData,
  hmUnit,
  kmUnit,
  date,
  onSuccess,
  onCancel,
  positionMap,
  limitInfo
}) {
  const notify = useNotifier();
  const { t } = useLanguage();

  // Main Form Default State
  const [reasonId, setReasonId] = useState(() => {
    if (!selectedTires || selectedTires.length === 0) return null;
    const firstDamage = selectedTires[0].lastInspection?.tire_damage;
    if (!firstDamage || firstDamage === 0) return null;
    const allSame = selectedTires.every((t) => t.lastInspection?.tire_damage === firstDamage);
    return allSame ? firstDamage : null;
  });

  const [currentCondition, setcurrentCondition] = useState('Good');
  const [mechanicName, setMechanicName] = useState('');
  const [reason, setReason] = useState('');
  const [bulkAirPressure, setBulkAirPressure] = useState('');
  const [bulkTreadDepth, setBulkTreadDepth] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch Options
  const reasonPickerParams = usePickerPagination({
    fetchHook: useTireDamageDataList,
    buildArgs: buildReasonArgs,
    formatOption: formatReasonOption
  });

  const prefilledDamages = useMemo(() => {
    const arr = [];
    if (selectedTires) {
      selectedTires.forEach((tire) => {
        if (tire.lastInspection?.tire_damage && tire.lastInspection?.tire_damage_name) {
          arr.push({
            value: tire.lastInspection.tire_damage,
            label: tire.lastInspection.tire_damage_name
          });
        }
      });
    }
    return arr;
  }, [selectedTires]);

  // Table State
  // tableData maps tire.id to its specific form data
  const [tableData, setTableData] = useState(() => {
    const initial = {};
    if (selectedTires) {
      selectedTires.forEach((tire) => {
        const inspectionData = tire.last_inspection || tire.lastInspection || {};
        const dmg = inspectionData.tire_damage;
        const inspectCond = inspectionData.condition;
        const existingAirPressureResult = getValidValueWithSource(
          tire.lastInspection?.air_pressure,
          tire.air_pressure,
          tire.join?.m_tire_spec_pressure_std || tire.pressure_std
        );
        const existingAirPressure = existingAirPressureResult.value;
        const airPressureSource = existingAirPressureResult.source;

        const existingTreadDepthResult = getValidValueWithSource(
          tire.lastInspection?.tread_depth,
          tire.tread_depth,
          tire.join?.m_tire_spec_original_tread_depth || tire.original_tread_depth
        );
        let defaultTreadDepth = existingTreadDepthResult.value || '';
        const treadDepthSource = existingTreadDepthResult.source;

        const originalTreadDepth = parseFloat(tire.join?.m_tire_spec_original_tread_depth || tire.original_tread_depth || 0);

        if (defaultTreadDepth !== '' && defaultTreadDepth != null) {
          let val = parseFloat(defaultTreadDepth);
          if (!isNaN(val) && originalTreadDepth && val > originalTreadDepth) {
            defaultTreadDepth = originalTreadDepth;
          }
        }
        initial[tire.id] = {
          reasonId: dmg && dmg !== 0 ? dmg : '',
          baseCondition: tire.base_condition || 'GOOD',
          currentCondition: inspectCond || 'Good',
          isInspectionDriven: !!inspectCond,
          mechanicName: '',
          reason: '',
          hmUnit: hmUnit,
          kmUnit: kmUnit,
          airPressure: existingAirPressure || '',
          airPressureSource,
          hasExistingAirPressure: airPressureSource === 'Last Inspection',
          treadDepth: defaultTreadDepth,
          treadDepthSource,
          hasExistingTreadDepth: treadDepthSource === 'Last Inspection',
          originalTreadDepth
        };
      });
    }
    return initial;
  });
  const [isCustom, setIsCustom] = useState(() => {
    const initial = {};
    if (selectedTires) {
      selectedTires.forEach((tire) => {
        const inspectionData = tire.last_inspection || tire.lastInspection || {};
        const dmg = inspectionData.tire_damage;
        initial[tire.id] = {
          reasonId: !!(dmg && dmg !== 0),
          currentCondition: !!inspectionData.condition
        };
      });
    }
    return initial;
  });

  // Update table data when main form changes, IF not custom
  useEffect(() => {
    setTableData((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((tireId) => {
        const customFlags = isCustom[tireId] || {};

        let bulkTreadVal = next[tireId].treadDepth;
        if (!next[tireId].hasExistingTreadDepth && bulkTreadDepth !== '' && !customFlags.treadDepth) {
          let val = parseFloat(bulkTreadDepth);
          const originalTreadDepth = next[tireId].originalTreadDepth;
          if (!isNaN(val) && originalTreadDepth && val > originalTreadDepth) {
            bulkTreadVal = originalTreadDepth;
          } else {
            bulkTreadVal = bulkTreadDepth;
          }
        }

        next[tireId] = {
          ...next[tireId],
          reasonId: customFlags.reasonId ? next[tireId].reasonId : reasonId || null,
          currentCondition: next[tireId].isInspectionDriven
            ? next[tireId].currentCondition
            : customFlags.currentCondition
              ? next[tireId].currentCondition
              : currentCondition,
          mechanicName: customFlags.mechanicName ? next[tireId].mechanicName : mechanicName,
          reason: customFlags.reason ? next[tireId].reason : reason,
          airPressure: customFlags.airPressure
            ? next[tireId].airPressure
            : next[tireId].hasExistingAirPressure
              ? next[tireId].airPressure
              : bulkAirPressure !== ''
                ? bulkAirPressure
                : next[tireId].airPressure,
          treadDepth: customFlags.treadDepth ? next[tireId].treadDepth : bulkTreadVal
        };
      });
      return next;
    });
  }, [reasonId, currentCondition, mechanicName, reason, bulkAirPressure, bulkTreadDepth, isCustom]);

  const handleTableChange = (tireId, field, value) => {
    setTableData((prev) => {
      let finalValue = value;
      if (field === 'treadDepth') {
        let val = parseFloat(value);
        const originalTreadDepth = prev[tireId].originalTreadDepth;
        if (!isNaN(val) && originalTreadDepth && val > originalTreadDepth) {
          finalValue = originalTreadDepth;
          notify({
            message: `${t('tireCycle.treadDepthCannotExceedOriginal', 'Tread depth cannot exceed original')} (${originalTreadDepth} mm)`,
            severity: 'warning'
          });
        } else if (isNaN(val)) {
          finalValue = value;
        } else {
          finalValue = val;
        }
      }
      return {
        ...prev,
        [tireId]: { ...prev[tireId], [field]: finalValue }
      };
    });
    setIsCustom((prev) => ({
      ...prev,
      [tireId]: { ...prev[tireId], [field]: true }
    }));
  };

  if (limitInfo && !limitInfo.allowedTx.includes('REMOVE')) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" fontWeight="bold">
          {`${t('tireCycle.blockedByBackdateRules', 'This transaction is blocked by backdate rules.')} (REMOVE)`}
        </Typography>
      </Box>
    );
  }

  const handleRemoveNow = async () => {
    if (!vehicleDetails) return notify({ message: t('tireCycle.selectValidVehicle', 'Select a valid vehicle'), severity: 'error' });
    if (!selectedTires || selectedTires.length === 0)
      return notify({ message: t('tireCycle.noTiresSelected', 'No tires selected'), severity: 'error' });

    // Validate table data
    for (const tire of selectedTires) {
      const data = tableData[tire.id] || {};
      const posLabel = positionMap[tire.posCode]?.label || tire.posCode;

      if (!data.kmUnit && data.kmUnit !== 0)
        return notify({
          message: `${posLabel}: ${t('tireCycle.odometerRequired', 'Km Unit is required')}`,
          severity: 'error'
        });
      if (!data.mechanicName)
        return notify({
          message: `${posLabel}: ${t('tireCycle.mechanicRequired', 'Mechanic name is required')}`,
          severity: 'error'
        });
      if (!data.reason)
        return notify({
          message: `${posLabel}: ${t('tireCycle.reasonNotesRequired', 'Reason notes are required')}`,
          severity: 'error'
        });
      if (!data.airPressure)
        return notify({
          message: `${posLabel}: ${t('tireCycle.airPressureRequired', 'Air Pressure is required')}`,
          severity: 'error'
        });
      if (!data.treadDepth)
        return notify({
          message: `${posLabel}: ${t('tireCycle.treadDepthRequired', 'Tread Depth is required')}`,
          severity: 'error'
        });
    }

    setIsSubmitting(true);
    let successfulCount = 0;

    // We process sequentially, updating the currentAxleState in memory for each to ensure state consistency
    let currentAxleState = JSON.parse(JSON.stringify(rawAxleData));

    for (const tire of selectedTires) {
      const data = tableData[tire.id];

      // Update local axle state
      if (currentAxleState && currentAxleState.info) {
        currentAxleState.info.forEach((axle) => {
          (axle.positions || []).forEach((pos) => {
            if (pos.code === tire.posCode) {
              pos.tire_item_id = 0;
              pos.serial_number = '';
            }
          });
        });
      }

      const payload = {
        company: vehicleDetails.company_id || vehicleDetails.company,
        vehicle: vehicleDetails.id,
        tire_equip_vehicle: tireEquipVehicleId,
        tire_item: tire.tire_item || tire.tire_item_id || tire.id,
        serial_number: tire.serial_number,
        transaction_type: 'REMOVE',
        remove_date: date || dayjs().format('YYYY-MM-DD'),
        current_position: {
          axle_info: currentAxleState,
          unit_info: {
            km_unit: parseFloat(data.kmUnit) || 0,
            hm_unit: parseFloat(data.hmUnit) || 0
          }
        },
        last_position: tire.posCode,
        base_condition: data.baseCondition,
        current_condition: data.currentCondition,
        mechanic_name: data.mechanicName,
        reason: data.reason,
        tire_damage: data.reasonId,
        km_unit: parseFloat(data.kmUnit) || 0,
        hm_unit: parseFloat(data.hmUnit) || 0,
        air_pressure: parseFloat(data.airPressure) || 0,
        tread_depth: parseFloat(data.treadDepth) || 0
      };

      try {
        await createTireTransaction(payload);
        successfulCount++;
      } catch (error) {
        const posLabel = positionMap[tire.posCode]?.label || tire.posCode;
        notify({
          message: `${t('tireCycle.failedToRemove', 'Failed to remove')} ${posLabel}: ${error.response?.data?.message || 'Unknown error'}`,
          severity: 'error'
        });
        break; // Stop processing further on error
      }
    }

    setIsSubmitting(false);

    if (successfulCount > 0) {
      notify({
        message: `${t('tireCycle.successfullyRemoved', 'Successfully removed')} ${successfulCount} ${t('tireCycle.tires', 'tires')}`,
        severity: 'success'
      });
      if (onSuccess) onSuccess();
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {t('tireCycle.removeSelectedTires', 'Remove Selected Tires')} ({selectedTires?.length || 0})
      </Typography>

      <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main' }}>
        {t('tireCycle.mainFormDefaults', 'Main Form (Defaults)')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            select
            fullWidth
            size="small"
            label={t('tireCycle.currentCondition', 'Current Condition *')}
            value={currentCondition}
            onChange={(e) => setcurrentCondition(e.target.value)}
          >
            <MenuItem value="Good">Good</MenuItem>
            <MenuItem value="Damaged">Damaged</MenuItem>
          </TextField>
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <PickerField
            label={t('tireCycle.tireDamage', 'Tire Damage')}
            {...reasonPickerParams}
            selectedItems={prefilledDamages}
            value={reasonId}
            onChange={(val) => setReasonId(val)}
            disabled
            helperText={t('tireCycle.autoFilledFromLastInspection', 'Auto-filled from last inspection')}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField fullWidth size="small" label={t('tireCardHistory.kmUnit', 'KM Unit')} value={kmUnit} disabled />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField fullWidth size="small" label={t('tireCardHistory.hmUnit', 'HM Unit')} value={hmUnit} disabled />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            size="small"
            label={t('tireCardHistory.mechanicName', 'Mechanic Name *')}
            value={mechanicName}
            onChange={(e) => setMechanicName(e.target.value)}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            size="small"
            label={t('tireCardHistory.reason', 'Reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={t('tireCycle.bulkAirPressure', 'Bulk Air Pressure')}
            value={bulkAirPressure}
            onChange={(e) => setBulkAirPressure(e.target.value)}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e') e.preventDefault();
            }}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={t('tireCycle.bulkTreadDepth', 'Bulk Tread Depth')}
            value={bulkTreadDepth}
            onChange={(e) => setBulkTreadDepth(e.target.value)}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e') e.preventDefault();
            }}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main' }}>
        {t('tireCycle.tiresToRemove', 'Tires to Remove (Customize per tire)')}
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell>{t('tireCycle.position', 'Position')}</TableCell>
              <TableCell>{t('tireCycle.tire', 'Tire')}</TableCell>
              <TableCell>{t('tireCycle.baseCondition', 'Base Condition')}</TableCell>
              <TableCell>{t('tireCycle.currentConditionTh', 'Current Condition')}</TableCell>
              <TableCell>{t('tireCycle.airPressureTh', 'Air Pressure')}</TableCell>
              <TableCell>{t('tireCycle.treadDepthTh', 'Tread Depth')}</TableCell>
              <TableCell>{t('tireCycle.tireDamageAutoFilled', 'Tire Damage (Auto-filled)')}</TableCell>
              <TableCell>{t('tireCycle.mechanic', 'Mechanic')}</TableCell>
              <TableCell>{t('tireCycle.notes', 'Notes')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(selectedTires || []).map((tire) => {
              const data = tableData[tire.id] || {};
              const posLabel = positionMap[tire.posCode]?.label || tire.posCode;
              return (
                <TableRow key={tire.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{posLabel}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{tire.serial_number}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Typography variant="body2">{data.baseCondition || 'GOOD'}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={data.currentCondition || ''}
                      onChange={(e) => handleTableChange(tire.id, 'currentCondition', e.target.value)}
                      disabled={data.isInspectionDriven}
                    >
                      <MenuItem value="Good">Good</MenuItem>
                      <MenuItem value="Damaged">Damaged</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={data.airPressure || ''}
                      onChange={(e) => handleTableChange(tire.id, 'airPressure', e.target.value)}
                      disabled={data.hasExistingAirPressure}
                      inputProps={{ min: 0 }}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'e') e.preventDefault();
                      }}
                      helperText={data.airPressureSource ? `${data.airPressureSource}` : ''}
                      FormHelperTextProps={{ sx: { fontSize: '0.65rem', whiteSpace: 'nowrap' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={data.treadDepth || ''}
                      onChange={(e) => handleTableChange(tire.id, 'treadDepth', e.target.value)}
                      disabled={data.hasExistingTreadDepth}
                      inputProps={{ min: 0 }}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'e') e.preventDefault();
                      }}
                      helperText={data.treadDepthSource ? `${data.treadDepthSource}` : ''}
                      FormHelperTextProps={{ sx: { fontSize: '0.65rem', whiteSpace: 'nowrap' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={data.reasonId || ''}
                      disabled
                      onChange={(e) => handleTableChange(tire.id, 'reasonId', e.target.value)}
                      // helperText="Auto-filled from last inspection"
                    >
                      <MenuItem value="">
                        <em>{t('tireCycle.none', 'None')}</em>
                      </MenuItem>
                      {reasonPickerParams.options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                      {tire.lastInspection?.tire_damage &&
                        tire.lastInspection?.tire_damage !== 0 &&
                        !reasonPickerParams.options.some((o) => o.value === tire.lastInspection.tire_damage) && (
                          <MenuItem value={tire.lastInspection.tire_damage}>
                            {tire.lastInspection.tire_damage_name || tire.lastInspection.tire_damage}
                          </MenuItem>
                        )}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={data.mechanicName || ''}
                      onChange={(e) => handleTableChange(tire.id, 'mechanicName', e.target.value)}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={data.reason || ''}
                      onChange={(e) => handleTableChange(tire.id, 'reason', e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          {t('general.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" color="error" onClick={handleRemoveNow} disabled={isSubmitting}>
          {(selectedTires || []).length > 1
            ? t('tireCycle.removeSelectedTiresS', 'Remove Selected Tires')
            : t('tireCycle.removeSelectedTire', 'Remove Selected Tire')}
        </Button>
      </Stack>
    </Box>
  );
}
